import React from 'react';
import {
    User,
    Sparkles,
    Check,
    X,
    Edit3,
    Undo2,
    AlertCircle,
    Loader2
} from 'lucide-react';
import InlineDataCard from './InlineDataCard';

/**
 * Simple markdown renderer for chat messages
 * Handles: **bold**, *italic*, `code`, - bullet points, numbered lists
 */
const renderMarkdown = (text) => {
    if (!text) return null;

    // Split into lines for processing
    const lines = text.split('\n');
    const elements = [];
    let listItems = [];
    let listType = null;

    const processInlineFormatting = (line, index) => {
        // Process inline formatting: **bold**, *italic*, `code`
        const parts = [];
        let remaining = line;
        let partIndex = 0;

        while (remaining.length > 0) {
            // Check for code first (backticks)
            const codeMatch = remaining.match(/`([^`]+)`/);
            // Check for bold
            const boldMatch = remaining.match(/\*\*([^*]+)\*\*/);
            // Check for italic (single asterisk, not part of bold)
            const italicMatch = remaining.match(/(?<!\*)\*([^*]+)\*(?!\*)/);

            // Find the earliest match
            let earliestMatch = null;
            let earliestIndex = remaining.length;
            let matchType = null;

            if (codeMatch && remaining.indexOf(codeMatch[0]) < earliestIndex) {
                earliestMatch = codeMatch;
                earliestIndex = remaining.indexOf(codeMatch[0]);
                matchType = 'code';
            }
            if (boldMatch && remaining.indexOf(boldMatch[0]) < earliestIndex) {
                earliestMatch = boldMatch;
                earliestIndex = remaining.indexOf(boldMatch[0]);
                matchType = 'bold';
            }
            if (italicMatch && remaining.indexOf(italicMatch[0]) < earliestIndex) {
                earliestMatch = italicMatch;
                earliestIndex = remaining.indexOf(italicMatch[0]);
                matchType = 'italic';
            }

            if (earliestMatch) {
                // Add text before the match
                if (earliestIndex > 0) {
                    parts.push(remaining.substring(0, earliestIndex));
                }

                // Add the formatted element
                if (matchType === 'code') {
                    parts.push(
                        <code key={`${index}-${partIndex++}`} style={{
                            background: 'rgba(0,0,0,0.1)',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontFamily: 'monospace',
                            fontSize: '0.9em'
                        }}>
                            {earliestMatch[1]}
                        </code>
                    );
                } else if (matchType === 'bold') {
                    parts.push(<strong key={`${index}-${partIndex++}`}>{earliestMatch[1]}</strong>);
                } else if (matchType === 'italic') {
                    parts.push(<em key={`${index}-${partIndex++}`}>{earliestMatch[1]}</em>);
                }

                remaining = remaining.substring(earliestIndex + earliestMatch[0].length);
            } else {
                // No more matches, add remaining text
                parts.push(remaining);
                remaining = '';
            }
        }

        return parts.length > 0 ? parts : line;
    };

    const flushList = () => {
        if (listItems.length > 0) {
            if (listType === 'ul') {
                elements.push(
                    <ul key={`list-${elements.length}`} style={{ margin: '8px 0', paddingLeft: '20px' }}>
                        {listItems}
                    </ul>
                );
            } else {
                elements.push(
                    <ol key={`list-${elements.length}`} style={{ margin: '8px 0', paddingLeft: '20px' }}>
                        {listItems}
                    </ol>
                );
            }
            listItems = [];
            listType = null;
        }
    };

    lines.forEach((line, index) => {
        // Check for bullet points
        const bulletMatch = line.match(/^[\s]*[-•]\s+(.+)/);
        // Check for numbered lists
        const numberedMatch = line.match(/^[\s]*(\d+)[.)]\s+(.+)/);

        if (bulletMatch) {
            if (listType !== 'ul') {
                flushList();
                listType = 'ul';
            }
            listItems.push(
                <li key={`li-${index}`} style={{ marginBottom: '4px' }}>
                    {processInlineFormatting(bulletMatch[1], index)}
                </li>
            );
        } else if (numberedMatch) {
            if (listType !== 'ol') {
                flushList();
                listType = 'ol';
            }
            listItems.push(
                <li key={`li-${index}`} style={{ marginBottom: '4px' }}>
                    {processInlineFormatting(numberedMatch[2], index)}
                </li>
            );
        } else {
            flushList();

            // Empty line = paragraph break
            if (line.trim() === '') {
                elements.push(<div key={`br-${index}`} style={{ height: '8px' }} />);
            } else {
                elements.push(
                    <div key={`p-${index}`}>
                        {processInlineFormatting(line, index)}
                    </div>
                );
            }
        }
    });

    // Flush any remaining list
    flushList();

    return elements;
};

/**
 * AdvisorMessage - Renders a single message in the advisor chat
 * Handles user messages, assistant messages, action proposals, and executed actions
 */
const AdvisorMessage = ({
    message,
    onApproveAction,
    onModifyAction,
    onSkipAction,
    onUndoAction,
    onNavigate,
    isActionPending = false
}) => {
    const isUser = message.role === 'user';
    const isSystem = message.role === 'system';

    // System messages (like summaries) are rendered differently
    if (isSystem) {
        return (
            <div style={{
                padding: 'var(--space-2) var(--space-4)',
                background: 'var(--color-surface)',
                borderRadius: 'var(--radius-md)',
                margin: 'var(--space-3) 0',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-tertiary)',
                fontStyle: 'italic',
                textAlign: 'center'
            }}>
                {message.content}
            </div>
        );
    }

    return (
        <div style={{
            display: 'flex',
            gap: 'var(--space-3)',
            marginBottom: 'var(--space-4)',
            flexDirection: isUser ? 'row-reverse' : 'row'
        }}>
            {/* Avatar */}
            <div style={{
                width: '32px',
                height: '32px',
                borderRadius: 'var(--radius-full)',
                background: isUser ? 'var(--color-surface-elevated)' : 'var(--gradient-primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
            }}>
                {isUser ? (
                    <User size={16} style={{ color: 'var(--color-text-secondary)' }} />
                ) : (
                    <Sparkles size={16} style={{ color: 'white' }} />
                )}
            </div>

            {/* Message content */}
            <div style={{
                flex: 1,
                maxWidth: '85%'
            }}>
                {/* Message bubble */}
                <div style={{
                    background: isUser ? 'var(--color-primary)' : 'var(--color-surface)',
                    color: isUser ? 'white' : 'var(--color-text-primary)',
                    padding: 'var(--space-3) var(--space-4)',
                    borderRadius: 'var(--radius-lg)',
                    borderTopLeftRadius: isUser ? 'var(--radius-lg)' : 'var(--radius-sm)',
                    borderTopRightRadius: isUser ? 'var(--radius-sm)' : 'var(--radius-lg)',
                    fontSize: 'var(--font-size-sm)',
                    lineHeight: 1.6,
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word'
                }}>
                    {isUser ? message.content : renderMarkdown(message.content)}
                </div>

                {/* Data cards (assistant only) */}
                {!isUser && message.dataCards && message.dataCards.length > 0 && (
                    <div style={{
                        marginTop: 'var(--space-3)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--space-2)'
                    }}>
                        {message.dataCards.map((card, index) => (
                            <InlineDataCard
                                key={index}
                                type={card.type}
                                data={card.data}
                            />
                        ))}
                    </div>
                )}

                {/* Proposed action (needs approval) */}
                {!isUser && message.proposedAction && !message.executedAction && (
                    <ActionProposal
                        action={message.proposedAction}
                        onApprove={onApproveAction}
                        onModify={onModifyAction}
                        onSkip={onSkipAction}
                        onNavigate={onNavigate}
                        isPending={isActionPending}
                    />
                )}

                {/* Executed action (with undo) */}
                {message.executedAction && (
                    <ExecutedAction
                        action={message.executedAction}
                        onUndo={onUndoAction}
                    />
                )}

                {/* Timestamp */}
                <div style={{
                    marginTop: 'var(--space-1)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-tertiary)',
                    textAlign: isUser ? 'right' : 'left'
                }}>
                    {formatTimestamp(message.timestamp)}
                </div>
            </div>
        </div>
    );
};

/**
 * ActionProposal - Renders a proposed action that needs user approval
 */
const ActionProposal = ({ action, onApprove, onModify, onSkip, isPending, onNavigate }) => {
    const getActionIcon = (type) => {
        switch (type) {
            case 'budget_change': return '💰';
            case 'pause': return '⏸️';
            case 'resume': return '▶️';
            case 'navigate': return '🔗';
            case 'create_campaign': return '🚀';
            default: return '⚡';
        }
    };

    const getActionLabel = (type) => {
        switch (type) {
            case 'navigate': return 'Quick Action';
            case 'create_campaign': return 'Create Campaign';
            default: return 'Proposed Action';
        }
    };

    // Navigation actions have a simpler UI - just a "Go" button
    const isNavigationAction = action.type === 'navigate' || action.type === 'create_campaign';

    return (
        <div style={{
            marginTop: 'var(--space-3)',
            background: isNavigationAction
                ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(59, 130, 246, 0.02))'
                : 'linear-gradient(135deg, rgba(124, 58, 237, 0.08), rgba(124, 58, 237, 0.02))',
            border: `1px solid ${isNavigationAction ? 'rgba(59, 130, 246, 0.2)' : 'rgba(124, 58, 237, 0.2)'}`,
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-3)',
            overflow: 'hidden'
        }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                marginBottom: 'var(--space-2)'
            }}>
                <span>{getActionIcon(action.type)}</span>
                <span style={{
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 600,
                    color: isNavigationAction ? 'var(--color-info)' : 'var(--color-primary)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                }}>
                    {getActionLabel(action.type)}
                </span>
            </div>

            <p style={{
                margin: 0,
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-primary)',
                fontWeight: 500
            }}>
                {action.description}
            </p>

            {action.campaignName && !isNavigationAction && (
                <p style={{
                    margin: 'var(--space-1) 0 0 0',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-secondary)'
                }}>
                    Campaign: {action.campaignName}
                </p>
            )}

            <div style={{
                display: 'flex',
                gap: 'var(--space-2)',
                marginTop: 'var(--space-3)'
            }}>
                {isNavigationAction ? (
                    <>
                        <button
                            onClick={() => onNavigate?.(action.params?.destination || '/campaign-builder', action.params?.campaignConfig)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)',
                                padding: 'var(--space-2) var(--space-3)',
                                background: 'var(--color-info)',
                                border: 'none',
                                borderRadius: 'var(--radius-md)',
                                color: 'white',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 500,
                                cursor: 'pointer'
                            }}
                        >
                            {action.type === 'create_campaign' ? 'Start Building' : 'Go There'}
                            <span style={{ marginLeft: '2px' }}>→</span>
                        </button>
                        <button
                            onClick={() => onSkip?.(action)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)',
                                padding: 'var(--space-2) var(--space-3)',
                                background: 'transparent',
                                border: 'none',
                                color: 'var(--color-text-tertiary)',
                                fontSize: 'var(--font-size-sm)',
                                cursor: 'pointer'
                            }}
                        >
                            Maybe later
                        </button>
                    </>
                ) : (
                    <>
                        <button
                            onClick={() => onApprove?.(action)}
                            disabled={isPending}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)',
                                padding: 'var(--space-2) var(--space-3)',
                                background: 'var(--color-primary)',
                                border: 'none',
                                borderRadius: 'var(--radius-md)',
                                color: 'white',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 500,
                                cursor: isPending ? 'not-allowed' : 'pointer',
                                opacity: isPending ? 0.7 : 1
                            }}
                        >
                            {isPending ? (
                                <Loader2 size={14} className="spin" />
                            ) : (
                                <Check size={14} />
                            )}
                            Approve & Apply
                        </button>

                        <button
                            onClick={() => onModify?.(action)}
                            disabled={isPending}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)',
                                padding: 'var(--space-2) var(--space-3)',
                                background: 'var(--color-surface-elevated)',
                                border: '1px solid var(--color-border)',
                                borderRadius: 'var(--radius-md)',
                                color: 'var(--color-text-primary)',
                                fontSize: 'var(--font-size-sm)',
                                cursor: isPending ? 'not-allowed' : 'pointer',
                                opacity: isPending ? 0.7 : 1
                            }}
                        >
                            <Edit3 size={14} />
                            Modify
                        </button>

                        <button
                            onClick={() => onSkip?.(action)}
                            disabled={isPending}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)',
                                padding: 'var(--space-2) var(--space-3)',
                                background: 'transparent',
                                border: 'none',
                                color: 'var(--color-text-tertiary)',
                                fontSize: 'var(--font-size-sm)',
                                cursor: isPending ? 'not-allowed' : 'pointer',
                                opacity: isPending ? 0.7 : 1
                            }}
                        >
                            <X size={14} />
                            Skip
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

/**
 * ExecutedAction - Renders a completed action with undo option
 */
const ExecutedAction = ({ action, onUndo }) => {
    const isUndone = !!action.undoneAt;

    return (
        <div style={{
            marginTop: 'var(--space-3)',
            background: isUndone
                ? 'var(--color-surface)'
                : 'linear-gradient(135deg, rgba(34, 197, 94, 0.08), rgba(34, 197, 94, 0.02))',
            border: `1px solid ${isUndone ? 'var(--color-border)' : 'rgba(34, 197, 94, 0.2)'}`,
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-3)'
        }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)'
                }}>
                    {isUndone ? (
                        <Undo2 size={16} style={{ color: 'var(--color-text-tertiary)' }} />
                    ) : (
                        <Check size={16} style={{ color: 'var(--color-success)' }} />
                    )}
                    <span style={{
                        fontSize: 'var(--font-size-sm)',
                        color: isUndone ? 'var(--color-text-tertiary)' : 'var(--color-text-primary)',
                        textDecoration: isUndone ? 'line-through' : 'none'
                    }}>
                        {action.description}
                    </span>
                </div>

                {action.undoable && !isUndone && (
                    <button
                        onClick={() => onUndo?.(action)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-1)',
                            padding: 'var(--space-1) var(--space-2)',
                            background: 'transparent',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-sm)',
                            color: 'var(--color-text-secondary)',
                            fontSize: 'var(--font-size-xs)',
                            cursor: 'pointer'
                        }}
                    >
                        <Undo2 size={12} />
                        Undo
                    </button>
                )}

                {isUndone && (
                    <span style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-tertiary)'
                    }}>
                        Undone
                    </span>
                )}
            </div>
        </div>
    );
};

/**
 * Format timestamp for display
 */
const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';

    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;

    return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    });
};

export default AdvisorMessage;
